import { FormikProps } from "formik";
import React from "react";
import { TripFormValues } from "../types/tripTypes";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Typography
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RichTextEditor from "components/shared/TextEditor/RTE";

interface TTCarryListProps {
  formik: FormikProps<TripFormValues>;
  editable?: boolean;
}

const TTCarryList: React.FC<TTCarryListProps> = ({ formik, editable = true }) => {
  const { values, setFieldValue } = formik;
  const [isExpanded, setExpanded] = React.useState<boolean>(true);

  const handleChange = (value: string) => {
    setFieldValue("thingsToCarry", value);
  };

  const handleAccordionToggle = (
    _event: React.SyntheticEvent,
    expanded: boolean
  ) => {
    setExpanded(expanded);
  };

  return (
    <Card
      sx={{
        marginTop: 3,
        padding: 2,
        width: "100%",
        marginX: "auto",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        borderRadius: 2,
        border: "1px solid #E8EAED",
      }}
    >
      <CardContent sx={{ padding: 3 }}>
        <Box sx={{ marginBottom: 3 }}>
          <Typography
            fontWeight={600}
            variant="h6"
            color="#181D27"
            sx={{ marginBottom: 0.5, fontSize: "20px" }}
          >
            Things To Carry
          </Typography>
          <Typography
            variant="body2"
            color="#535862"
            sx={{ fontSize: "14px", lineHeight: 1.5 }}
          >
            Suggest a list of things to carry, for traveller to make the best of the trip.
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Accordion
            expanded={isExpanded}
            onChange={handleAccordionToggle}
            sx={{
              "&:before": { display: "none" },
              border: "1px solid #E0E3E7",
              borderRadius: "12px !important",
              boxShadow: isExpanded
                ? "0 4px 12px rgba(0,0,0,0.1)"
                : "0 1px 3px rgba(0,0,0,0.05)",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                borderColor: "#C1C7CD",
              },
            }}
          >
            <AccordionSummary
              expandIcon={
                <ExpandMoreIcon
                  sx={{
                    color: "#535862",
                    transition: "transform 0.2s ease-in-out",
                    flexShrink: 0,
                  }}
                />
              }
              sx={{
                margin: 0,
                padding: "8px 20px",
                minHeight: "60px",
                "&.Mui-expanded": {
                  minHeight: "60px",
                },
                "& .MuiAccordionSummary-content": {
                  minWidth: 0,
                  overflow: "hidden",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  minWidth: 0,
                  gap: 2,
                  flex: 1,
                  overflow: "hidden",
                }}
              >
                <Typography
                  variant="body1"
                  color="#181D27"
                  fontWeight={600}
                  sx={{
                    flexShrink: 0,
                    fontSize: "16px",
                  }}
                >
                  Things To Carry
                </Typography>
                {!isExpanded && (
                  <Typography
                    variant="body2"
                    color="#9CA3AF"
                    sx={{
                      fontSize: "14px",
                      fontStyle: "italic",
                    }}
                  >
                    Add Things To Carry details 
                  </Typography>
                )}
              </Box>
            </AccordionSummary>

            <AccordionDetails sx={{ padding: "0 20px 20px 20px" }}>
              <Box sx={{ borderTop: "1px solid #F3F4F6", paddingTop: 3 }}>
                <Typography
                  variant="subtitle2"
                  fontWeight={500}
                  sx={{
                    marginBottom: 1.5,
                    color: "#374151",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  Description *
                </Typography>
                <RichTextEditor
                  value={values.thingsToCarry || ""}
                  onChange={handleChange}
                  placeholder="Provide detailed information about the things to carry like documents, cloths, toiletries..."
                  disabled={!editable}
                  maxLength={2000}
                  minHeight={140}
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
      </CardContent>
    </Card>
  );
};

export default TTCarryList;
